// Wizard branding loader.
//
// Fetches `/api/wizard/branding/:retailerId` (public endpoint, no auth) and
// applies the result to the document root as CSS custom properties so every
// step inherits the retailer's look without needing the branding object
// passed down explicitly.
//
// The backend always returns defaults for unknown retailers (see
// `kitchenos-api/modules/wizard_branding.js`), so a 200 is the happy path
// and any non-200 means the wizard should still render with built-in
// defaults rather than failing hard.

import type { AppointmentTypesConfig, HeroImageSource, RetailerBranding } from "../types";

const DEFAULT_BRANDING: RetailerBranding = {
  retailerId: "",
  primary: "#E8760A",
  secondary: "#1a1a1c",
  text: "#ffffff",
  logoUrl: "",
  fontFamily: "Inter",
  buttonStyle: "rounded",
  welcomeHeading: "Design your dream kitchen in 90 seconds",
  welcomeSubtext:
    "Answer a few quick questions and we'll get back to you with ideas, prices and lead times.",
  showPoweredBy: true,
  radius: "8px",
  isDefault: true,
  customerRenderEnabled: false,
  splashHeadline: "Let's plan your kitchen",
  splashCtaLabel: "Get started",
  reviewHeadline: "Thank you — that's everything we need.",
  reviewSubhead:
    "Have a check through what you've told us, then submit and your specialist will be in touch within one working day.",
  whatHappensNext:
    "We'll create your personal kitchen portal so you can chat with your designer, view photos and approve quotes — all in one place.",
  heroImageSource: "gallery",
  heroImages: [],
};

interface BrandingResponse {
  retailerId: string;
  branding: {
    primary: string;
    secondary: string;
    text: string;
    logoUrl: string;
    fontFamily: string;
    buttonStyle: "rounded" | "square" | "pill";
    welcomeHeading: string;
    welcomeSubtext: string;
    showPoweredBy: boolean;
    splashHeadline?: string;
    splashCtaLabel?: string;
    reviewHeadline?: string;
    reviewSubhead?: string;
    whatHappensNext?: string;
    heroImageSource?: HeroImageSource;
    heroImages?: string[];
  };
  radius: string;
  isDefault: boolean;
  /** Global customer-render feature flag. */
  customerRenderEnabled?: boolean;
  /** Per-retailer appointment-type toggles (migration 0072). */
  appointmentTypes?: AppointmentTypesConfig;
  /** Optional fields the backend may include later for the "or call us" fallback. */
  phone?: string;
  businessName?: string;
}

/** Load + sanitise the retailer's branding. Never throws — returns defaults. */
export async function loadBranding(retailerId: string): Promise<RetailerBranding> {
  const fallback: RetailerBranding = { ...DEFAULT_BRANDING, retailerId };
  if (!retailerId) return fallback;

  try {
    // Cache-bust the branding fetch so a Settings → Branding colour change is
    // never masked by a stale browser/intermediary cache — the wizard always
    // pulls the live PG value on load. (Pairs with the API's no-cache header.)
    const res = await fetch(
      `/api/wizard/branding/${encodeURIComponent(retailerId)}?t=${Date.now()}`,
      { credentials: "omit", cache: "no-store", headers: { Accept: "application/json" } },
    );
    if (!res.ok) return fallback;
    const body = (await res.json()) as BrandingResponse;
    if (!body || !body.branding) return fallback;
    return {
      retailerId: body.retailerId || retailerId,
      primary: body.branding.primary || fallback.primary,
      secondary: body.branding.secondary || fallback.secondary,
      text: body.branding.text || fallback.text,
      logoUrl: body.branding.logoUrl || "",
      fontFamily: body.branding.fontFamily || fallback.fontFamily,
      buttonStyle: body.branding.buttonStyle || fallback.buttonStyle,
      welcomeHeading: body.branding.welcomeHeading || fallback.welcomeHeading,
      welcomeSubtext: body.branding.welcomeSubtext || fallback.welcomeSubtext,
      showPoweredBy: body.branding.showPoweredBy !== false,
      radius: body.radius || fallback.radius,
      isDefault: !!body.isDefault,
      customerRenderEnabled: body.customerRenderEnabled === true,
      // Copy: an older API build won't send these at all, so fall back to the
      // shipped defaults rather than rendering an empty headline.
      splashHeadline: body.branding.splashHeadline || fallback.splashHeadline,
      splashCtaLabel: body.branding.splashCtaLabel || fallback.splashCtaLabel,
      reviewHeadline: body.branding.reviewHeadline || fallback.reviewHeadline,
      reviewSubhead: body.branding.reviewSubhead || fallback.reviewSubhead,
      whatHappensNext: body.branding.whatHappensNext || fallback.whatHappensNext,
      heroImageSource: body.branding.heroImageSource === "custom" ? "custom" : "gallery",
      heroImages: Array.isArray(body.branding.heroImages)
        ? body.branding.heroImages.filter((u): u is string => typeof u === "string" && !!u).slice(0, 6)
        : [],
      ...(body.appointmentTypes && typeof body.appointmentTypes === "object"
        ? { appointmentTypes: body.appointmentTypes }
        : {}),
      ...(body.phone ? { phone: body.phone } : {}),
      ...(body.businessName ? { businessName: body.businessName } : {}),
    };
  } catch {
    return fallback;
  }
}

/** Write the branding into CSS variables on :root, plus a few helpers. */
export function applyBranding(b: RetailerBranding): void {
  const r = document.documentElement.style;
  r.setProperty("--brand-primary", b.primary);
  r.setProperty("--brand-secondary", b.secondary);
  r.setProperty("--brand-text", b.text);
  r.setProperty("--btn-radius", b.radius);
  r.setProperty("--brand-font", `"${b.fontFamily}"`);

  // Title + favicon update so the iframe parent + bookmarked tab look right.
  if (b.businessName) {
    document.title = `${b.businessName} — Kitchen Design Tool`;
  }
}
